import Radio from "../../../UI/Radio";
import { useFormikContext } from "formik";
import TextInput from "../../../UI/TextInput";
import Tooltip from "../../../UI/Tooltip";
import { Interrogation } from "../../../UI/Icons";
import { currencyFormatter } from "../../../../api/Functions";
import { useEffect } from "react";
import TextArea from "../../../UI/TextArea";
import { useTenantAccountBalance } from "../../../../hooks/use-balances";
import IsLoading from "../../../UI/IsLoading";

export default function RestitutionDGForm({ property, tenant }) {
  const { values, setFieldValue } = useFormikContext();

  const { balances: balanceTenant, isLoading } = useTenantAccountBalance(
    tenant?.id,
    false,
  );

  const soldeLocataire = balanceTenant?.balance ?? 0;
  const montantImpaye = soldeLocataire < 0 ? -soldeLocataire : 0;
  const montantTropPercu = soldeLocataire > 0 ? soldeLocataire : 0;

  const hasDegradation = values.has_degradations === "true";
  const hasProvision = values.conserver_provision === "true";
  const hasImpaye = montantImpaye > 0;
  const hasTropPercu = montantTropPercu > 0;

  const degradationFlag = hasDegradation ? 1 : 0;
  const provisionFlag = hasProvision ? 1 << 1 : 0;
  const impayeFlag = hasImpaye ? 1 << 2 : 0;

  const montantReparations = hasDegradation
    ? parseFloat(values.montant_reparations) || 0
    : 0;

  const multiplicateurDG = hasProvision ? 0.8 : 1;

  const paragrapheRetenueReparation = `
      L'état des lieux effectué fait apparaitre un certain nombre de réparations à votre charge,
      dont vous trouverez la liste ci-après. <br />
      Liste des réparations (indiquez le montant de la retenue effectuée 
      auprès du locataire après déduction de la vétusté) : <br />
      ${values.degradations.replaceAll("\n", "<br />")} <br />
      Nous vous informons, en application des dispositions de l'article 22 de la loi du 6 juillet 1989, 
      que la somme de ${currencyFormatter(montantReparations)},
      représentant le montant de la remise en état à effectuer, sera retenue sur le dépôt de garantie.<br />`;

  const paragrapheRetenueProvision = `
      L’arrêté annuel des charges de l’immeuble n’ayant pas encore été établi, nous vous informons, 
      en application de l’article 22 de la loi n° 89-462 du 6 juillet 1989, 
      qu'une retenue de 20% est opérée sur la somme de ${currencyFormatter(
        balanceTenant?.dg_debit,
      )} que vous avez versée au titre du dépôt de garantie.
      La retenue effectuée s’élève donc à la somme de ${currencyFormatter(
        balanceTenant?.dg_debit * 0.2 + montantReparations + montantImpaye,
      )}.
      Dans le mois qui suit l'arrêté définitif des charges de l’immeuble, 
      il vous sera restitué la retenue de 20% du dépôt de garantie,
      déduction faite le cas échéant de la régularisation des charges.`;

  const paragrapheRetenueImpaye = `
      ${
        hasDegradation ? "Par ailleurs" : "Toutefois"
      }, vous n'êtes plus à jour de vos paiements pour un montant total de
      ${currencyFormatter(
        montantImpaye,
      )} charges comprises. En conséquence, et conformément à l’article 22 de la loi n° 89-462 du 6 juillet 1989,
      nous retiendrons ${
        hasDegradation ? " également " : ""
      } cette somme sur votre dépôt de garantie.`;

  const paragrapheTropPercu = `
      Votre compte locataire présente par ailleurs un trop-perçu de
      ${currencyFormatter(
        montantTropPercu,
      )} au titre du loyer et/ou des charges,
      qui vous sera restitué en complément du dépôt de garantie.`;

  const paragrapheNoDegradation = `
      Le logement ayant été restitué en bon état de location, aucune retenue
      n'est effectuée sur le dépôt de garantie au titre des réparations
      locatives.`;

  const paragrapheCoordonneesBancaires = `
      Pour vous restituer ${
        hasProvision || hasDegradation ? " le solde " : " la totalité "
      }
      du dépôt de garantie${
        hasTropPercu ? " ainsi que le trop-perçu" : ""
      }, je vous remercie de nous transmettre vos coordonnées bancaires afin d'effectuer un virement
    `;

  const montantRestitution =
    balanceTenant?.dg_debit * multiplicateurDG -
    montantReparations -
    montantImpaye +
    montantTropPercu;

  const montantDuParLocataire =
    montantReparations +
    montantImpaye -
    balanceTenant?.dg_debit -
    montantTropPercu;

  const buildRecapitulatif = (totalLabel, totalAmount) => {
    const lignes = [
      `Dépôt de garantie versé : ${currencyFormatter(balanceTenant?.dg_debit)}`,
    ];
    if (montantTropPercu > 0) {
      lignes.push(
        `Trop-perçu sur loyer et/ou charges à restituer : + ${currencyFormatter(
          montantTropPercu,
        )}`,
      );
    }
    if (montantReparations > 0) {
      lignes.push(
        `Retenue pour réparations : - ${currencyFormatter(montantReparations)}`,
      );
    }
    if (montantImpaye > 0) {
      lignes.push(
        `Retenue sur solde du compte locataire (loyers et/ou charges impayés) : - ${currencyFormatter(
          montantImpaye,
        )}`,
      );
    }
    if (hasProvision) {
      lignes.push(
        `Retenue provisoire (20% du dépôt de garantie) : - ${currencyFormatter(
          balanceTenant?.dg_debit * (1 - multiplicateurDG),
        )}`,
      );
    }
    return `
      <strong>Détail du calcul :</strong><br />
      ${lignes.join("<br />")}<br />
      <strong>${totalLabel} : ${currencyFormatter(totalAmount)}</strong>
    `;
  };

  const paragrapheRestitutionPartielle = `
      ${paragrapheCoordonneesBancaires} de la somme de ${currencyFormatter(
        montantRestitution,
      )}.
      <br /><br />
      ${buildRecapitulatif(
        "Total à restituer au locataire",
        montantRestitution,
      )}
    `;

  const paragrapheDGInsuffisant = `
      Le dépôt de garantie versé ne suffit pas à couvrir les retenues détaillées ci-dessous.
      <br /><br />
      ${buildRecapitulatif("Reste dû par le locataire", montantDuParLocataire)}
      <br /><br />
      Je vous remercie de m'adresser la somme de ${currencyFormatter(
        montantDuParLocataire,
      )} dans un délai de huit jours à compter de la réception du présent courrier.
      Passé ce délai, je serai contraint d'engager toute procédure nécessaire.`;

  const textLoyerImpaye = <>Le locataire ne semble pas à jour de son loyer.</>;

  const textTropPercu = (
    <>
      Le locataire dispose d'un trop-perçu de{" "}
      {currencyFormatter(montantTropPercu)} au titre du loyer et/ou des charges,
      à lui restituer en complément du dépôt de garantie.
    </>
  );

  const textDGInsuffisant = (
    <>
      {montantImpaye > 0 && textLoyerImpaye} Le dépôt de garantie
      {hasTropPercu && " (même complété du trop-perçu)"} n’est pas suffisant
      pour couvrir {montantReparations > 0 && " les réparations"}
      {montantReparations > 0 && montantImpaye > 0 && " et "}
      {montantImpaye > 0 && " les loyers impayés"}
      {hasProvision && " plus les 20% de provision"}.
    </>
  );

  const textLocataireRedevance = (
    <>
      {textDGInsuffisant} Après déduction des retenues
      {hasTropPercu && " et prise en compte du trop-perçu"}, le locataire vous
      doit la somme de {currencyFormatter(montantDuParLocataire)}.
    </>
  );

  const textDGProvisionInsuffisant = (
    <>
      {textDGInsuffisant} Après déduction des retenues à effectuer, vous pouvez
      conserver seulement{" "}
      {currencyFormatter(
        balanceTenant?.dg_debit - montantReparations - montantImpaye,
      )}{" "}
      du dépôt de garantie pour les provisions.
      {hasTropPercu
        ? ` Vous devez toutefois lui reverser le trop-perçu de ${currencyFormatter(
            montantTropPercu,
          )} au titre du loyer et/ou des charges.`
        : " Vous n'avez donc rien à lui reverser pour le moment."}
    </>
  );

  const textDeduction = (
    <>
      {montantImpaye > 0 && textLoyerImpaye}
      {hasTropPercu && <> {textTropPercu}</>} Après
      {hasTropPercu && " prise en compte du trop-perçu et"} déduction des
      retenues à effectuer, vous devez restituer{" "}
      {currencyFormatter(montantRestitution)} au locataire
      {hasProvision && " et conserver 20% du dépôt de garantie"}
      {hasProvision && " en attente de l'appel de charge"}. Pour rappel vous
      devez lui restituer dans un délai de <strong>deux mois</strong> à compter
      de la remise des clés.
    </>
  );

  const textRappelDelai = (
    <>
      Pour rappel, vous devez lui restituer dans un délai{" "}
      <strong>d’un mois</strong> à compter de la remise des clés.
    </>
  );

  let text = "";
  let courrier = "";

  switch (impayeFlag | provisionFlag | degradationFlag) {
    case 0: // 000
      text = hasTropPercu ? (
        <>
          Le locataire est à jour de son loyer et des charges, et dispose d'un
          trop-perçu de {currencyFormatter(montantTropPercu)}. Vous devez lui
          restituer la totalité du dépôt de garantie (
          {currencyFormatter(balanceTenant?.dg_debit)}) ainsi que le trop-perçu,
          soit {currencyFormatter(montantRestitution)} au total.{" "}
          {textRappelDelai}
        </>
      ) : (
        <>
          Le locataire est à jour de son loyer et des charges. Vous pouvez lui
          restituer la totalité du dépôt de garantie (
          {currencyFormatter(balanceTenant?.dg_debit)}). {textRappelDelai}
        </>
      );
      courrier = hasTropPercu
        ? `${paragrapheNoDegradation} ${paragrapheTropPercu} ${paragrapheRestitutionPartielle}`
        : `${paragrapheNoDegradation} ${paragrapheCoordonneesBancaires}.`;
      break;
    case 1: // 001
      if (montantReparations > balanceTenant?.dg_debit + montantTropPercu) {
        text = textLocataireRedevance;
        courrier = `${paragrapheRetenueReparation} ${
          hasTropPercu ? paragrapheTropPercu + " " : ""
        }${paragrapheDGInsuffisant}`;
      } else {
        text = textDeduction;
        courrier = `${paragrapheRetenueReparation} ${
          hasTropPercu ? paragrapheTropPercu + " " : ""
        }${paragrapheRestitutionPartielle}`;
      }
      break;
    case 2: // 010
      text = hasTropPercu ? (
        <>
          Le locataire est à jour de son loyer et des charges, et dispose d'un
          trop-perçu de {currencyFormatter(montantTropPercu)}. Vous pouvez lui
          restituer son dépôt de garantie et conserver 20% en attente de l'appel
          de charge. En comptant le trop-perçu, vous devez lui rendre{" "}
          {currencyFormatter(montantRestitution)} au total. {textRappelDelai}
        </>
      ) : (
        <>
          Le locataire est à jour de son loyer et des charges. Vous pouvez lui
          restituer son dépôt de garantie, et conserver 20% en attente de
          l'appel de charge. Vous devez donc lui rendre{" "}
          {currencyFormatter(balanceTenant?.dg_debit * multiplicateurDG)}.{" "}
          {textRappelDelai}
        </>
      );
      courrier = hasTropPercu
        ? `${paragrapheNoDegradation} ${paragrapheRetenueProvision} ${paragrapheTropPercu} ${paragrapheRestitutionPartielle}`
        : `${paragrapheNoDegradation} ${paragrapheRetenueProvision} ${paragrapheCoordonneesBancaires}.`;
      break;
    case 3: // 011
      if (montantReparations > balanceTenant?.dg_debit + montantTropPercu) {
        text = textLocataireRedevance;
        courrier = `${paragrapheRetenueReparation} ${
          hasTropPercu ? paragrapheTropPercu + " " : ""
        }${paragrapheDGInsuffisant}`;
      } else if (
        montantReparations >
        balanceTenant?.dg_debit * multiplicateurDG + montantTropPercu
      ) {
        text = textDGProvisionInsuffisant;
        courrier = `${paragrapheRetenueReparation} ${paragrapheRetenueProvision} ${
          hasTropPercu ? paragrapheTropPercu + " " : ""
        }${paragrapheRestitutionPartielle}`;
      } else {
        text = textDeduction;
        courrier = `${paragrapheRetenueReparation} ${paragrapheRetenueProvision} ${
          hasTropPercu ? paragrapheTropPercu + " " : ""
        }${paragrapheRestitutionPartielle}`;
      }
      break;
    case 4: // 100
      if (montantImpaye > balanceTenant?.dg_debit) {
        text = textLocataireRedevance;
        courrier = `${paragrapheNoDegradation} ${paragrapheRetenueImpaye} ${paragrapheDGInsuffisant}`;
      } else {
        text = textDeduction;
        courrier = `${paragrapheNoDegradation} ${paragrapheRetenueImpaye} ${paragrapheRestitutionPartielle}`;
      }
      break;
    case 5: // 101
      if (montantReparations + montantImpaye > balanceTenant?.dg_debit) {
        text = textLocataireRedevance;
        courrier = `${paragrapheRetenueReparation} ${paragrapheRetenueImpaye} ${paragrapheDGInsuffisant}`;
      } else {
        text = textDeduction;
        courrier = `${paragrapheRetenueReparation} ${paragrapheRetenueImpaye} ${paragrapheRestitutionPartielle}`;
      }
      break;
    case 6: // 110
      if (montantImpaye > balanceTenant?.dg_debit) {
        text = textLocataireRedevance;
        courrier = `${paragrapheRetenueImpaye} ${paragrapheDGInsuffisant}`;
      } else if (montantImpaye > balanceTenant.dg_debit * multiplicateurDG) {
        text = textDGProvisionInsuffisant;
        courrier = `${paragrapheRetenueImpaye} ${paragrapheRetenueProvision} ${paragrapheRestitutionPartielle}`;
      } else {
        text = textDeduction;
        courrier = `${paragrapheRetenueImpaye} ${paragrapheRetenueProvision} ${paragrapheRestitutionPartielle}`;
      }
      break;
    case 7: // 111
      if (montantImpaye + montantReparations > balanceTenant?.dg_debit) {
        text = textLocataireRedevance;
        courrier = `${paragrapheRetenueReparation} ${paragrapheRetenueImpaye} ${paragrapheDGInsuffisant}`;
      } else if (
        montantImpaye + montantReparations >
        balanceTenant?.dg_debit * multiplicateurDG
      ) {
        text = textDGProvisionInsuffisant;
        courrier = `${paragrapheRetenueReparation} ${paragrapheRetenueImpaye} 
        ${paragrapheRetenueProvision} ${paragrapheRestitutionPartielle}`;
      } else {
        text = textDeduction;
        courrier = `${paragrapheRetenueReparation} ${paragrapheRetenueImpaye}
        ${paragrapheRetenueProvision} ${paragrapheRestitutionPartielle}`;
      }
      break;
    default:
      break;
  }
  text = (
    <>
      {text} <br />
      <br />
      <p>
        En générant ce courrier, les différents appels seront créés dans
        l'historique des paiements du locataire. Si vous avez une partie ou la
        totalité du DG à lui rendre, il faudra créer la ligne (ou l'affecter si
        vos comptes bancaires sont synchronisés) dans la catégorie{" "}
        <strong>"Remboursement dépôt de garantie"</strong>.
      </p>
    </>
  );

  useEffect(() => {
    setFieldValue("courrier_text", courrier);
  }, [courrier, setFieldValue]);

  if (isLoading) return <IsLoading />;

  return (
    <div className={"marginB20"}>
      L’état des lieux de sortie fait-il apparaître de l'usure ou des
      dégradations ?
      <div className={"flex marginT10 marginB20"}>
        <Radio
          name={"has_degradations"}
          value="true"
          label="Oui"
          className={"marginL20"}
        />
        <Radio
          name={"has_degradations"}
          value="false"
          label="Non"
          className={"marginL20"}
        />
      </div>
      {hasDegradation && (
        <>
          <TextInput
            name={"montant_reparations"}
            type="number"
            step={"0.01"}
            unit={"€"}
            label={
              <>
                Montant des réparations à la charge du locataire{" "}
                <Tooltip
                  content={
                    <div>
                      <p className={"cardTitle marginB10"}>
                        Que pouvez-vous facturer ?
                      </p>
                      <p>Toute somme retenue doit être justifiée soit :</p>
                      <ul>
                        <li>par un devis</li>
                        <li>par une facture</li>
                      </ul>
                      <p className={"marginT10"}>
                        Vous pouvez facturer les produits que vous avez achetés
                        pour faire le ménage ou les réparations vous-même. Vous
                        ne pouvez pas imputer de “forfait” pour le temps passé.
                        Dans ce cas faites faire un devis par un professionnel
                        pour pouvoir le déduire.
                      </p>
                      <p className={"marginT10"}>
                        Vous devez communiquer les justificatifs au locataire à
                        sa première demande.
                      </p>
                    </div>
                  }
                >
                  <Interrogation />
                </Tooltip>
              </>
            }
          />
          <TextArea
            type="text"
            name="degradations"
            label="Liste des dégradations"
            rows="4"
            placeholder="Meuble abimé, ménage non fait, ..."
          />
        </>
      )}
      {property.type_propriete === "copropriete" && (
        <>
          Souhaitez-vous conserver une provision de 20% du dépôt de garantie en
          attente de l’arrêté annuel des comptes de l'immeuble ?
          <div className={"flex marginT10 marginB20"}>
            <Radio
              name={"conserver_provision"}
              value="true"
              label="Oui"
              className={"marginL20"}
            />
            <Radio
              name={"conserver_provision"}
              value="false"
              label="Non"
              className={"marginL20"}
            />
          </div>
        </>
      )}
      {text}
    </div>
  );
}
